// ValidacionGastosPanel.js — informe bajo demanda para validar la
// clasificación de gastos (movimientos_banco + gastos_efectivo_manual, por
// config_reglas_gastos): cuánto hay en cada categoría por año y cuánto queda
// sin clasificar, con la opción de asignarlo a mano o crear una regla desde
// ahí mismo (tal como se pidió: "visibles en Calidad del dato... y opción de
// crear regla desde ahí"). No se carga sola — es una consulta amplia (hasta
// 2 años) que no hace falta repetir en cada visita.
import React, { useState } from 'react';
import { authService } from '../../services/authService';
import { CATEGORIAS_GASTO } from './ReglasGastosPanel';

function fmtEur(n) {
  if (n == null) return '—';
  return Number(n).toLocaleString('es-ES', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + ' €';
}

function FilaSinClasificar({ item, onResuelto }) {
  const [modo, setModo] = useState(null); // null | 'asignar' | 'regla'
  const [categoria, setCategoria] = useState('otros_gastos');
  const [patron, setPatron] = useState(item.concepto?.split(' ').slice(0, 2).join(' ') || '');
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState('');

  const asignar = async () => {
    setGuardando(true); setError('');
    try {
      const res = await fetch('/.netlify/functions/asignarCategoriaMovimiento', {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
        body: JSON.stringify({ movimientoId: item.id, categoria }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error);
      onResuelto();
    } catch (e) { setError(e.message); } finally { setGuardando(false); }
  };

  const crearRegla = async () => {
    if (!patron.trim()) return;
    setGuardando(true); setError('');
    try {
      const res = await fetch('/.netlify/functions/saveReglaGasto', {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
        body: JSON.stringify({ patron, categoria, ivaPct: categoria === 'material' ? 21 : undefined }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error);
      onResuelto();
    } catch (e) { setError(e.message); } finally { setGuardando(false); }
  };

  return (
    <li className="text-xs border-b border-gray-50 py-2">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <span className="text-gray-600">{item.fecha} · <span className="font-mono">{item.concepto}</span> · <span className="font-semibold">{fmtEur(item.importe)}</span></span>
        {item.origen === 'banco' && !modo && (
          <div className="flex gap-2">
            <button onClick={() => setModo('asignar')} className="text-blue-600 hover:underline">Asignar categoría</button>
            <button onClick={() => setModo('regla')} className="text-blue-600 hover:underline">Crear regla</button>
          </div>
        )}
      </div>
      {modo && (
        <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
          {modo === 'regla' && (
            <input className="border rounded px-1.5 py-1 text-[11px] w-40" value={patron} onChange={e => setPatron(e.target.value)} placeholder="Patrón a buscar" />
          )}
          <select className="border rounded px-1.5 py-1 text-[11px]" value={categoria} onChange={e => setCategoria(e.target.value)}>
            {CATEGORIAS_GASTO.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
          </select>
          <button onClick={modo === 'asignar' ? asignar : crearRegla} disabled={guardando} className="text-[11px] font-bold text-emerald-600 hover:underline disabled:opacity-60">
            {guardando ? 'Guardando…' : 'Guardar'}
          </button>
          <button onClick={() => setModo(null)} className="text-[11px] text-gray-400 hover:underline">Cancelar</button>
          {error && <span className="text-[11px] text-red-600">{error}</span>}
        </div>
      )}
    </li>
  );
}

export default function ValidacionGastosPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [desde, setDesde] = useState('2025-01-01');

  const validar = async () => {
    setLoading(true); setError('');
    try {
      const res = await fetch(`/.netlify/functions/getDiagnosticoClasificacionGastos?desde=${desde}`, { headers: { ...authService.authHeader() } });
      const d = await res.json();
      if (!d.success) throw new Error(d.error);
      setData(d);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const anios = data ? Object.keys(data.porAnio).sort() : [];

  return (
    <div className="bg-white rounded-2xl border shadow-sm p-5">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <h3 className="font-bold text-gray-900 text-sm">Validar clasificación de gastos</h3>
        <div className="flex items-center gap-2">
          <input type="date" className="border rounded-lg px-2 py-1.5 text-xs" value={desde} onChange={e => setDesde(e.target.value)} />
          <button onClick={validar} disabled={loading} className="px-3 py-2 bg-gray-100 text-gray-700 text-xs font-bold rounded-xl hover:bg-gray-200 disabled:opacity-60">
            {loading ? 'Calculando…' : 'Validar'}
          </button>
        </div>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-2 text-sm mb-3">{error}</div>}

      {!data && !loading && !error && (
        <p className="text-[11px] text-gray-400">Pulsa "Validar" para ver, sobre los movimientos reales desde la fecha indicada: cuánto hay en cada categoría por año, y cuánto queda sin clasificar.</p>
      )}

      {data && (
        <div className="space-y-4">
          <p className="text-[11px] text-gray-400">{data.totalSalidas} gastos (banco + efectivo) entre {data.desde} y {data.hasta}.</p>

          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b bg-gray-50/60">
                  <th className="text-left px-3 py-1.5 font-bold text-gray-500 uppercase">Categoría</th>
                  {anios.map(a => <th key={a} colSpan={2} className="text-center px-3 py-1.5 font-bold text-gray-500 uppercase">{a}</th>)}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {CATEGORIAS_GASTO.map(c => (
                  <tr key={c.key}>
                    <td className="px-3 py-1.5 font-semibold text-gray-700">{c.label}</td>
                    {anios.map(a => {
                      const v = data.porAnio[a].porCategoria[c.key];
                      return (
                        <React.Fragment key={a}>
                          <td className="px-2 py-1.5 text-right">{v.count}</td>
                          <td className="px-2 py-1.5 text-right text-gray-500">{fmtEur(v.total)}</td>
                        </React.Fragment>
                      );
                    })}
                  </tr>
                ))}
                <tr className="bg-red-50">
                  <td className="px-3 py-1.5 font-bold text-red-700">Sin clasificar</td>
                  {anios.map(a => (
                    <React.Fragment key={a}>
                      <td className="px-2 py-1.5 text-right font-bold text-red-700">{data.porAnio[a].sinClasificarCount}</td>
                      <td className="px-2 py-1.5 text-right font-bold text-red-700">{fmtEur(data.porAnio[a].sinClasificarTotal)}</td>
                    </React.Fragment>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>

          {data.sinClasificar.length > 0 && (
            <div>
              <h4 className="text-xs font-bold text-red-700 uppercase mb-2">Sin clasificar ({data.sinClasificar.length})</h4>
              <ul>
                {data.sinClasificar.map((item, i) => (
                  <FilaSinClasificar key={item.id || i} item={item} onResuelto={validar} />
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
